'use client';
import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import Echo from 'laravel-echo';
import Pusher from 'pusher-js';
import { UserContext } from './user-context';

// Declare Pusher on window for Laravel Echo
declare global {
    interface Window {
        Pusher: typeof Pusher;
    }
}

export interface EchoContextValue {
    echo: Echo<any> | null;
    isConnected: boolean;
}

export interface EchoProviderProps {
    children: ReactNode;
}

export const EchoContext = createContext<EchoContextValue | undefined>(undefined);

export const EchoProvider = ({ children }: EchoProviderProps): JSX.Element => {
    const [echo, setEcho] = useState<Echo<any> | null>(null);
    const [isConnected, setIsConnected] = useState(false);
    const userContext = useContext(UserContext);

    useEffect(() => {
        // Only initialize Echo if user is authenticated
        if (!userContext?.auth?.accessToken) {
            if (echo) {
                echo.disconnect();
                setEcho(null);
                setIsConnected(false);
            }
            return;
        }

        // Check if Reverb configuration is available
        if (!process.env.NEXT_PUBLIC_REVERB_APP_KEY || !process.env.NEXT_PUBLIC_REVERB_HOST) {
            console.warn('Reverb configuration is missing. WebSocket will not be initialized.');
            return;
        }

        // Make Pusher available globally
        window.Pusher = Pusher;

        // Clean token - remove surrounding quotes if present
        const cleanToken = userContext.auth.accessToken?.replace(/^["']|["']$/g, '') || '';

        // Determine environment and configuration
        const isProduction = process.env.APP_ENV === 'production' || process.env.NODE_ENV === 'production';
        const reverbPort = process.env.NEXT_PUBLIC_REVERB_PORT ? process.env.NEXT_PUBLIC_REVERB_PORT : isProduction ? '443' : '8080';
        //const wsPath = process.env.NEXT_PUBLIC_REVERB_PATH || (isProduction ? '/pusher' : '');
        const wsPath = isProduction ? (process.env.NEXT_PUBLIC_REVERB_PATH || '/pusher') : '';

        // Initialize Laravel Echo
        const echoInstance = new Echo({
            broadcaster: 'reverb',
            key: process.env.NEXT_PUBLIC_REVERB_APP_KEY,
            wsHost: process.env.NEXT_PUBLIC_REVERB_HOST,
            wsPath: wsPath || undefined,
            wsPort: reverbPort,
            wssPort: reverbPort,
            forceTLS: isProduction,
            enabledTransports: ['ws', 'wss'],
            authEndpoint: `${process.env.BASE_URL}/broadcasting/auth`,
            auth: {
                headers: {
                    Authorization: `Bearer ${cleanToken}`,
                    Accept: 'application/json',
                },
            },
        });

        setEcho(echoInstance);

        // Listen for connection events
        const pusherConnection = (echoInstance.connector as any).pusher.connection;

        pusherConnection.bind('connected', () => {
            console.log('WebSocket connected');
            setIsConnected(true);
        });

        pusherConnection.bind('disconnected', () => {
            console.log('WebSocket disconnected');
            setIsConnected(false);
        });

        pusherConnection.bind('error', (error: any) => {
            console.error('WebSocket error:', error);
            setIsConnected(false);
        });

        // Cleanup on unmount
        return () => {
            if (echoInstance) {
                echoInstance.disconnect();
            }
        };
    }, [userContext?.auth?.accessToken]);

    return <EchoContext.Provider value={{ echo, isConnected }}>{children}</EchoContext.Provider>;
};

export const useEcho = () => {
    const context = useContext(EchoContext);
    if (context === undefined) {
        throw new Error('useEcho must be used within an EchoProvider');
    }
    return context;
};
